import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FlatList, Keyboard, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { DS } from '@/design-system'

export type TournamentMessage = {
  _id: string
  userId: string
  userName: string
  text: string
  // Message ordering uses Convex's auto `_creationTime`. The chat list is
  // inverted (newest at the bottom of the screen, FlatList renders top-to-
  // bottom in inverted space) so auto-scroll falls out of the layout — new
  // messages appear at index 0 of the inverted data.
  _creationTime?: number
}

const MAX_MESSAGE_LENGTH = 500

interface Props {
  messages: TournamentMessage[]
  currentUserId: string
  /** Whether the input is editable. False for non-participants. */
  canChat: boolean
  /** Sends a chat message. The hook is responsible for try/catch + toast. */
  onSend: (text: string) => Promise<void>
  /** Controlled value so the parent can clear/restore on mutation result. */
  text: string
  onChangeText: (next: string) => void
  /** True while the send mutation is in flight (disables button). */
  pending: boolean
}

/**
 * Lobby chat list + input. Extracted from TournamentLobbyScreen so the
 * primary lobby component doesn't have to re-render the FlatList every
 * render-cycle of the parent (e.g. when the per-second tick fires).
 *
 * Wrapped in `React.memo` so this whole subtree (including the inverted
 * FlatList + its `useMemo`'d `invertedMessages`) only re-renders when
 * one of the props' references actually changes. Callers must pass
 * stable references for `messages` (use a module-level `EMPTY_MESSAGES`
 * constant for the empty case rather than `messages ?? []` inline) and
 * stable callbacks (`onSend`, `onChangeText`) from `useCallback` or a
 * stable hook.
 */
export const TournamentLobbyChat = React.memo(function TournamentLobbyChat({
  messages,
  currentUserId,
  canChat,
  onSend,
  text,
  onChangeText,
  pending
}: Props) {
  const listRef = useRef<FlatList>(null)
  const insets = useSafeAreaInsets()

  // Inverted-list data: render newest first so FlatList's natural top-to-
  // bottom placement (in inverted space) puts new messages at the visible
  // bottom of the screen. Auto-scroll becomes a no-op — newer rows mount
  // at index 0 and slide existing rows up.
  //
  // Defensively reverse-by-`_creationTime` so out-of-order server pushes
  // can't put a freshly inserted message above an older one in the visible
  // list. The server already returns ascending order; this guards against
  // race-driven reordering.
  const invertedMessages = useMemo(() => {
    return [...messages].sort((a, b) => (b._creationTime ?? 0) - (a._creationTime ?? 0))
  }, [messages])

  // Keyboard-height-aware padding for the input row. We do this directly via
  // `Keyboard` listeners rather than KeyboardAvoidingView so both platforms
  // behave identically and the iOS autocomplete accessory bar is included in
  // the measurement (`endCoordinates.height` on iOS covers keyboard + bar).
  //
  // iOS fires `keyboardWillShow/Hide` (synchronous with animation); Android
  // only fires `keyboardDidShow/Hide`. Subscribe to both so the right event
  // wins on each platform.
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'
    const showSub = Keyboard.addListener(showEvent, (e) => {
      setKeyboardHeight(e.endCoordinates.height)
    })
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0)
    })
    return () => {
      showSub.remove()
      hideSub.remove()
    }
  }, [])

  const renderMessage = useCallback(
    ({ item }: { item: TournamentMessage }) => {
      const isMe = item.userId === currentUserId
      return (
        <View style={[styles.messageBubble, isMe && styles.messageBubbleMe]}>
          {!isMe && (
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
              {item.userName}
            </DS.Text>
          )}
          <DS.Text variant="Body Large/Regular">{item.text}</DS.Text>
        </View>
      )
    },
    [currentUserId]
  )

  const trimmed = text.trim()
  const canSend = canChat && !pending && trimmed.length > 0
  const placeholder = canChat ? 'Send message' : 'Register to chat'

  return (
    <View style={styles.chatContainer}>
      <View style={styles.chatDivider} />
      <FlatList
        ref={listRef}
        data={invertedMessages}
        renderItem={renderMessage}
        keyExtractor={(item) => item._id}
        style={styles.chatList}
        contentContainerStyle={styles.chatListContent}
        // Inverted: index 0 is the newest message and renders at the
        // visible bottom of the list. No explicit scrollToEnd needed.
        inverted
        // Bound the work for low-end Android devices. Full chat is capped at
        // ~200 messages by the server, but virtualization still helps.
        initialNumToRender={20}
        maxToRenderPerBatch={20}
        windowSize={10}
        removeClippedSubviews
      />
      {/*
        Pad by the keyboard height MINUS the bottom safe-area inset (the screen
        is wrapped in SafeAreaView which already accounts for the home indicator
        / nav bar). Plus 8pt of breathing room so the input doesn't sit flush
        against the keyboard / autocomplete bar.
      */}
      <View
        style={[
          styles.chatInputRow,
          keyboardHeight > 0 && { paddingBottom: Math.max(8, keyboardHeight - insets.bottom + 8) }
        ]}
      >
        <TextInput
          style={styles.chatInput}
          placeholder={placeholder}
          placeholderTextColor="#B2AFA9"
          value={text}
          onChangeText={onChangeText}
          onSubmitEditing={() => canSend && onSend(trimmed)}
          returnKeyType="send"
          submitBehavior="submit"
          editable={canChat && !pending}
          maxLength={MAX_MESSAGE_LENGTH}
          accessibilityLabel="Tournament chat input"
        />
        <Pressable
          onPress={() => canSend && onSend(trimmed)}
          disabled={!canSend}
          style={[styles.sendButton, !canSend && { opacity: 0.4 }]}
          accessibilityRole="button"
          accessibilityLabel="Send chat message"
        >
          <DS.Text variant="Mini/Bold" style={{ color: '#fff' }}>Send</DS.Text>
        </Pressable>
      </View>
    </View>
  )
})

const styles = StyleSheet.create({
  chatContainer: { flex: 1 },
  chatDivider: { height: 1, backgroundColor: DS.palette['Brand/Light Grey'] },
  chatList: { flex: 1 },
  chatListContent: { padding: 16, gap: 8 },
  chatInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: DS.palette['Brand/Light Grey']
  },
  chatInput: {
    flex: 1,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 16,
    backgroundColor: DS.palette['Brand/Cream'],
    fontSize: 14
  },
  sendButton: {
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8
  },
  messageBubble: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
    maxWidth: '80%'
  },
  messageBubbleMe: {
    backgroundColor: DS.palette['Brand/Light Grey'],
    alignSelf: 'flex-end'
  }
})
